import { Badge } from '@/components/UI';
import { formatCount } from './helpers';
import type { ScorecardGenerationAuditCoverage } from './types';

const AuditCoverageStat = ({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: string }) => {
    const toneClass =
        tone === 'good' ? 'text-green-400' : tone === 'warn' ? 'text-yellow-400' : tone === 'bad' ? 'text-red-400' : 'text-zinc-200';

    return (
        <div>
            <div className="text-xs text-zinc-500">{label}</div>
            <div className={`text-sm font-semibold ${toneClass}`}>{value}</div>
        </div>
    );
};

interface ScorecardAuditCoveragePanelProps {
    coverage: ScorecardGenerationAuditCoverage;
}

const ScorecardAuditCoveragePanel = ({ coverage }: ScorecardAuditCoveragePanelProps) => (
    <div className={`rounded border p-3 ${coverage.warning ? 'border-yellow-900/70 bg-yellow-950/20' : 'border-zinc-800 bg-zinc-900'}`}>
        <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-zinc-200">Durable External Feature Audit</div>
            <Badge variant={coverage.auditFresh ? 'success' : 'warning'}>{coverage.auditFresh ? 'fresh' : 'stale'}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
            <AuditCoverageStat
                label="Fresh"
                tone={coverage.externalFreshRows >= coverage.diagnosticsRows ? 'good' : 'neutral'}
                value={`${formatCount(coverage.externalFreshRows)}/${formatCount(coverage.diagnosticsRows)}`}
            />
            <AuditCoverageStat label="Reviewed" value={formatCount(coverage.externalReviewedRows)} />
            <AuditCoverageStat
                label="Missing"
                tone={coverage.externalMissingRows > 0 ? 'warn' : 'good'}
                value={formatCount(coverage.externalMissingRows)}
            />
            <AuditCoverageStat
                label="Stale"
                tone={coverage.externalStaleRows > 0 ? 'warn' : 'good'}
                value={formatCount(coverage.externalStaleRows)}
            />
        </div>
        {coverage.warning && (
            <div className="mt-3 border-t border-yellow-900/40 pt-2 text-xs leading-5 text-yellow-300">{coverage.warning}</div>
        )}
    </div>
);

export default ScorecardAuditCoveragePanel;
